/**
 * InfoModal.js - 정보/성공 메시지 모달
 * Enter 키로 닫을 수 있는 간단한 알림 모달
 */

import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { CheckCircle } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

const InfoModal = ({ 
  isOpen, 
  onClose, 
  title = '알림', 
  message, 
  type = 'success' // 'success', 'info', 'warning'
}) => {
  const { colors, isDark } = useCustomTheme();
  const isClosingRef = useRef(false);

  // Enter 키로 닫기
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && isOpen && !isClosingRef.current) {
        e.preventDefault();
        e.stopPropagation();
        isClosingRef.current = true;
        
        // 모달 닫기
        onClose();
        
        // 잠시 후 플래그 초기화
        setTimeout(() => {
          isClosingRef.current = false;
        }, 300);
      }
    };

    if (isOpen) {
      isClosingRef.current = false;
      window.addEventListener('keydown', handleKeyDown, true); // capture phase에서 처리
      return () => window.removeEventListener('keydown', handleKeyDown, true);
    }
  }, [isOpen, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={48} color={colors.success || '#4caf50'} />;
      case 'info':
        return <CheckCircle size={48} color={colors.accent} />;
      case 'warning':
        return <CheckCircle size={48} color={colors.warning || '#ff9800'} />;
      default:
        return <CheckCircle size={48} color={colors.accent} />;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        // Backdrop 클릭이나 ESC 키로만 닫히도록 제한
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          onClose();
        }
      }}
      maxWidth="xs"
      fullWidth
      disableRestoreFocus={true}
      onKeyDown={(e) => {
        // Dialog 내부에서 키 이벤트 전파 막기
        if (e.key === 'Enter') {
          e.stopPropagation();
        }
      }}
      PaperProps={{
        sx: {
          backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
          backgroundImage: 'none',
        }
      }}
    >
      <DialogTitle sx={{ 
        color: colors.primaryText,
        fontSize: '18px',
        fontWeight: 600,
        pb: 1,
      }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 2,
          py: 2,
        }}>
          {getIcon()}
          <Typography 
            variant="body1" 
            sx={{ 
              color: colors.primaryText,
              textAlign: 'center',
              fontSize: '16px',
              whiteSpace: 'pre-line', // 줄바꿈 지원
            }}
          >
            {message}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isClosingRef.current) {
              isClosingRef.current = true;
              onClose();
              setTimeout(() => {
                isClosingRef.current = false;
              }, 300);
            }
          }}
          onKeyDown={(e) => {
            // 버튼에서도 Enter 키 이벤트 전파 막기
            if (e.key === 'Enter') {
              e.stopPropagation();
            }
          }}
          variant="contained"
          fullWidth
          autoFocus
          sx={{
            backgroundColor: colors.accent,
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: colors.accent,
              opacity: 0.9,
            },
          }}
        >
          확인 (Enter)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InfoModal;

